import {
	AEMContent,
	Card,
	Col,
	Divider,
	FieldWrapper,
	Input,
	Paginator,
	TableBody,
	TableCell,
	TableHead,
	TableHeaderCell,
	TextLink,
} from '@mlc/web-ui-toolkit';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
	PlanTransaction,
	PlanTransactionItem,
	PlanTransactions,
} from '../../../../../logic/domains/planProfile.domain';
import {
	fetchTransactionsInBatches,
	getPlanTransactions,
} from '../../../../../logic/functions/planProfile.function';
import {
	asCurrency,
	asFormattedDate,
	unicodeMinusToMinus,
} from '../../../../common/formatter';
import LoadingIndicator from '../../../../common/LoadingIndicator';
import ScrollableTable from '../../../Common/ScrollableTable/ScrollableTable';
import ApiError from '../../../Error/ApiError';
import NoMatching from '../Common/NoMatching';
import { MemberBlocked } from '../Common/StaticContent';
import planProfileTestIds from '../PlanProfile.elements';
import DownloadCsv from '../Common/DownloadCsv';
import Tab from '../Common/Tab';
import QuickFilter from '../Common/QuickFilter';
import { TableRow } from '../../../SearchResults/AllResultsTabContent/SearchResultsRow/styled';
import * as Styled from './styled';

type Props = {
	planUUID: string;
	planNumber: string;
	planName: string;
	balanceAsOfDate: string;
};

const MemberTransactions: FunctionComponent<Props> = ({
	planUUID,
	planNumber,
	planName,
	balanceAsOfDate,
}) => {
	const [memberTransactions, setMemberTransactions] = useState<
		PlanTransactions
	>();
	const [isInitLoading, setIsInitLoading] = useState(true);
	const [isPageLoading, setIsPageLoading] = useState(false);
	const [isFilter, setIsFilter] = useState(false);
	const [search, setSearch] = useState('');
	const [totalCount, setTotalCount] = useState<number>(0);
	const [paginatorKey, setPaginatorKey] = useState<number>(0);

	const PAGE_SIZE = 25;

	useEffect(() => {
		const fetchData = async () => {
			const memberTransactions = await getPlanTransactions(
				planUUID,
				1,
				PAGE_SIZE
			);
			setMemberTransactions(memberTransactions);
			if (memberTransactions) {
				setTotalCount(memberTransactions.totalCount);
			}
			setIsInitLoading(false);
		};
		fetchData();
	}, []);

	const filter = async (phrase: string) => {
		setIsFilter(true);
		phrase = phrase.replace(', ', ' ');
		const memberTransactions = await getPlanTransactions(
			planUUID,
			1,
			PAGE_SIZE,
			phrase
		);
		setSearch(phrase);
		setMemberTransactions(memberTransactions);
		setPaginatorKey(Math.random());
	};
	const updatePage = async (nextStartIndex: number) => {
		const nextPage = nextStartIndex / PAGE_SIZE + 1;
		setIsPageLoading(true);
		setMemberTransactions(
			await getPlanTransactions(planUUID, nextPage, PAGE_SIZE, search)
		);
		setIsPageLoading(false);
	};

	if (isInitLoading) return <LoadingIndicator />;

	if (!memberTransactions) return <ApiError />;

	if (!isFilter && memberTransactions.transactions.length === 0)
		return <NoMatching />;

	const transactions = applyFormatting(memberTransactions.transactions);
	return (
		<Tab title="Member transactions">
			<Card justifyContent="space-between" display="flex" alignItems="center">
				<SearchCol col={6}>
					<QuickFilter placeholder="Type name" fetchData={filter} />
				</SearchCol>
				<DownloadCsv
					options={{
						fileName: `EMP_DETAILS_${planNumber}`,
						title: [`Plan Name,${planName}`, `Plan No.,${planNumber}`],
						fields: createCsvFields(balanceAsOfDate),
					}}
					fetchDataToDownload={async () =>
						applyFormatting(
							await fetchTransactionsInBatches(planUUID, totalCount)
						)
					}
				/>
			</Card>
			<Paginator
				totalItems={memberTransactions.totalCount}
				onPageSelect={updatePage}
				key={paginatorKey}
			>
				{isPageLoading ? (
					<LoadingIndicator />
				) : (
					<ScrollableTable
						topScrollId="memberBenefitsTopScroll"
						containerProps={{
							marginBottom: 4,
							style: { maxHeight: '700px' },
							id: 'memberTransactionsContainer',
						}}
						tableProps={{
							variant: 'bottomLines',
							border: 'mlcBorderThin',
							borderColor: 'mlcAlmostWhite',
							layout: 'auto',
							summary: `Table with 9 columns and ${memberTransactions.transactions.length} rows displaying member benefits information`,
							id: 'memberTransactionsTable',
						}}
					>
						<TableHead>
							<Styled.NoWrapTR>
								<Styled.UnalignedHeaderCell scope="col">
									Name
								</Styled.UnalignedHeaderCell>
								<TableHeaderCell
									scope="col"
									data-id={
										planProfileTestIds.memberTransactionsOpeningBalHeader
									}
								>
									Opening balance
									<br />
									as at {asFormattedDate(balanceAsOfDate)}
								</TableHeaderCell>
								<TableHeaderCell>
									+Amounts In
									<br />
									Deposits
								</TableHeaderCell>
								<TableHeaderCell>Premiums</TableHeaderCell>
								<TableHeaderCell>
									-Amounts Out
									<br />
									Fees
								</TableHeaderCell>
								<TableHeaderCell>Taxes</TableHeaderCell>
								<TableHeaderCell>Payment</TableHeaderCell>
								<TableHeaderCell>
									Closing
									<br />
									Earnings
								</TableHeaderCell>
								<TableHeaderCell>Balance</TableHeaderCell>
							</Styled.NoWrapTR>
						</TableHead>
						<TableBody data-id={planProfileTestIds.memberTransactions}>
							{buildTableBody(transactions)}
						</TableBody>
					</ScrollableTable>
				)}
			</Paginator>
			<AEMContent
				variant="disclaimer"
				path="/secure/_content/Adviser/plan-profile/member-transactions/disclaimer"
			/>
		</Tab>
	);
};

const buildTableBody = (transactions: PlanTransaction[]) =>
	transactions.length > 0 ? (
		transactions.map((transaction, idx) => (
			<Styled.NoWrapTR
				key={idx}
				data-id={planProfileTestIds.memberTransactionsTableRow}
			>
				<Styled.UnalignedCell
					data-id={planProfileTestIds.memberInvestorNameLink}
				>
					{isMemberBlocked(transaction.investorName) ? (
						transaction.investorName
					) : (
						<TextLink
							target="_blank"
							fontWeight="semiBold"
							href={`/online/adviser/clientprofile/${transaction.customerNumber}`}
						>
							{transaction.investorName}
						</TextLink>
					)}
				</Styled.UnalignedCell>
				<TableCell data-id={planProfileTestIds.transactionOpeningBalance}>
					{transaction.openingBalance}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionAmountsInDeposit}>
					{transaction.contributionAmount}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionPremiums}>
					{transaction.premiumAmount}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionAmountsOutFees}>
					{transaction.fees}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionTaxes}>
					{transaction.taxAmount}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionPayment}>
					{transaction.payment}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionClosingEarnings}>
					{transaction.closingEarnings}
				</TableCell>
				<TableCell data-id={planProfileTestIds.transactionBalance}>
					{transaction.closingBalance}
				</TableCell>
			</Styled.NoWrapTR>
		))
	) : (
		<TableRow>
			<TableCell colspan="100%">No matching records found</TableCell>
		</TableRow>
	);

const isMemberBlocked = (name: string) => MemberBlocked === name;

const applyFormatting = (
	transactions: PlanTransactionItem[]
): PlanTransaction[] =>
	transactions.map((item: PlanTransactionItem) => {
		const isBlocked = isMemberBlocked(item.surname);
		return {
			customerNumber: item.customerNumber,
			investorName: isBlocked
				? item.surname
				: item.surname.concat(', ', item.givenName),
			openingBalance: asCurrency(item.openingBalance),
			contributionAmount: asCurrency(item.contributionAmount),
			premiumAmount: asCurrency(item.premiumAmount),
			fees: asCurrency(item.fees),
			taxAmount: asCurrency(item.taxAmount),
			payment: asCurrency(item.payment),
			closingEarnings: asCurrency(item.closingEarnings),
			closingBalance: asCurrency(item.closingBalance),
		};
	});

const createCsvFields = (balanceAsOfDate: string) => [
	{
		name: 'investorName',
	},
	{
		name: 'openingBalance',
		header: 'Opening balance as at ' + asFormattedDate(balanceAsOfDate),
	},
	{
		name: 'contributionAmount',
		header: 'Amounts In Deposits',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'premiumAmount',
		header: 'Premiums',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'fees',
		header: 'Amounts Out Fees',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'taxAmount',
		header: 'Taxes',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'payment',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'closingEarnings',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'closingBalance',
		header: 'Balance',
		apply: unicodeMinusToMinus,
	},
];

const SearchCol = styled(Col)`
	padding-left: 0px;
`;

export default MemberTransactions;
